<template>
  <NavigationLayout
    :navigationBarStyle="{
      'border-bottom': '1px solid #e8e8e8',
      top: 'var(--safe-area-inset-top, 0px)',
    }"
    :navigationContentStyle="{
      'padding-top': 'calc(44px + var(--safe-area-inset-top, 0px))',
    }"
  >
    <div
      id="mypage__wrapper"
      :class="localeClass"
      slot="contents"
      v-show="ready"
    >
      <div id="mypage__container">
        <section>
          <div class="title">
            <p>{{ $t('order_details__navigation') }}</p>
          </div>

          <div class="account">
            <div class="label">
              <b>{{ $t('order_details__label__account') }}</b>
            </div>
            <div class="info" v-if="ready">
              <div class="icon">
                <icon-image
                  :iconUrl="user != null ? user.iconUrl : ''"
                  size="33"
                />
              </div>
              <div class="username">
                {{ user != null ? user.username : '' }}
              </div>
            </div>
          </div>
          <hr />
          <div class="product" v-if="product">
            <div v-if="isCoinBonusProduct && bonusAmount > 0">
              <coin-amount :amount="product.totalGem + bonusAmount" />
            </div>
            <div v-else>
              <coin-amount :amount="product.totalGem" />
            </div>
            <div class="info">
              <div class="price">
                {{ product.formattedPrice }}
              </div>
            </div>
          </div>
          <div class="coin_info_paid_free" v-if="product">
            <span
              v-if="
                !isMonthlyCoinProduct(product) && // 月額コイン商品ではない
                isCampaignProduct(product) && // キャンペーン商品である
                isCoinBonusProduct && // 次月コイン増量適用商品
                bonusAmount > 0 && // ボーナスコインがある
                product.chargeGem > 0 && // 有償コインがある
                product.freeGem > 0 // 無償コインがある
              "
            >
              {{
                $t('coinSummaryCharge', {
                  chargeCoin: this.formatNumber(product.chargeGem),
                })
              }}
              <br />
              {{
                $t('coinSummaryCampaignFree', {
                  freeCoin: this.formatNumber(product.freeGem),
                })
              }}
            </span>
            <span v-else-if="product.chargeGem > 0 && product.freeGem > 0">
              {{
                $t('coinSummaryPaidFree', {
                  chargeCoin: this.formatNumber(product.chargeGem),
                  freeCoin: this.formatNumber(product.freeGem),
                })
              }}
            </span>
            <span v-else-if="product.chargeGem > 0">
              {{ $t('coinCharge') }}{{ this.formatNumber(product.chargeGem)
              }}{{ $t('coinName') }}
            </span>
          </div>
          <div
            v-if="isCoinBonusProduct && bonusAmount > 0"
            class="coin_purchase_bonus_info"
          >
            <p class="coin_purchase_bonus_info_text">
              {{
                $t('coinSummaryMonthlyGradeBonusFree', {
                  freeCoin: this.formatNumber(bonusAmount),
                })
              }}
            </p>
          </div>
          <hr />
          <div v-show="isCoinBonusProduct">
            <!-- 購入可能回数が初回のみ（１回）の場合 -->
            <p
              v-if="coinPurchaseBonus && coinPurchaseBonus.remainingTimes == 1"
              class="el_monthlyGradeBonus_text"
            >
              {{
                $t('coinPurchaseBonusMonthlyGradeBonusFirstLimitedValidNote')
              }}
            </p>
            <!-- 購入可能回数が複数回の場合 -->
            <p
              v-else-if="
                coinPurchaseBonus && coinPurchaseBonus.remainingTimes > 1
              "
              class="el_monthlyGradeBonus_text"
            >
              {{
                $t('coinPurchaseBonusMonthlyGradeBonusLimitedCountValidNote', {
                  count: coinPurchaseBonus.remainingTimes,
                })
              }}
            </p>
          </div>
          <div
            v-show="
              isKonbiniPaymentAvailableIPRegion &&
              !isKonbiniPaymentAvailableProduct &&
              konbiniPaymentMinPrice != 0
            "
          >
            <p class="el_konbiniPayment_text">
              {{
                $t(
                  'order_details__caption__convenience_store__payment_usage_restrictions',
                  {
                    price: konbiniPaymentMinPrice,
                  }
                )
              }}
            </p>
            <!-- /.el_konbiniPayment_text -->
          </div>
        </section>

        <section>
          <div class="payment_method">
            <span class="product_heading">{{ $t('paymentMethod') }}</span>
            <span
              class="edit"
              :class="showCardForm ? 'select_link_disabled' : null"
              @click="openCardModal"
            >
              {{ $t('order_details__text_link__edit') }}
              <img src="/img/pen.svg" />
            </span>
          </div>
          <div class="payment_explanation">
            <div>
              <p
                v-if="
                  isKonbiniPaymentAvailableProduct && isPayPayAvailableProduct
                "
              >
                {{
                  $t(
                    'order_details__body__method_of_payment__convenience_paypay_store'
                  )
                }}
              </p>
              <p v-else-if="isKonbiniPaymentAvailableProduct">
                {{ $t('order_details__body__method_of_payment') }}
              </p>
              <p v-else-if="isPayPayAvailableProduct">
                {{ $t('order_details__body__method_of_payment__paypay_store') }}
              </p>
              <p v-else>
                {{ $t('order_details__body__method_of_payment') }}
              </p>
              <p>{{ $t('order_details__body__available_card') }}</p>
            </div>
          </div>
        </section>

        <div class="select_method">
          <div :disabled="showCardForm" v-if="primaryCard">
            <div
              v-for="method in paymentMethods"
              :key="method.id"
              class="card_item"
            >
              <input
                type="radio"
                :value="method.id"
                :id="method.id"
                v-model="methodId"
                :disabled="processing || formRefreshing"
                @click="updatePrimaryCard(method.id)"
              />
              <label :for="method.id">
                <img
                  class="payment_method_icon"
                  :src="getPaymentMethodImg(method)"
                />
                {{ getCardInfo(method) }}
              </label>
            </div>
          </div>
          <div
            class="select_item"
            v-if="primaryCard || isPayPayAvailableProduct"
          >
            <input
              type="radio"
              value="new"
              id="new"
              :disabled="processing || formRefreshing"
              v-model="methodId"
              @click="updateCardSaveCheckbox()"
            />
            <label for="new" :disabled="processing || formRefreshing">
              {{ getPaymentMethodText() }}
            </label>
          </div>
          <card-modal
            :showModal="showCardModal"
            :paymentMethods="paymentMethods"
            :primaryMethodId="primaryMethodId"
            @initForm="initForm"
            @updateMethods="updateMethods"
            @updatePrimary="updatePrimary"
            @close="closeCardModal"
          />
        </div>

        <div class="payment_explanation" v-show="methodId == 'new'">
          <div class="available_payment_methods">
            <ul>
              <li><img src="/img/payment_method_icons/Visa.png" /></li>
              <li>
                <img src="/img/payment_method_icons/Mastercard.png" />
              </li>
              <li><img src="/img/payment_method_icons/JCB.png" /></li>
              <li><img src="/img/payment_method_icons/amex.png" /></li>
              <li><img src="/img/payment_method_icons/diners.png" /></li>
              <li>
                <img src="/img/payment_method_icons/discover.png" />
              </li>
              <li><img src="/img/payment_method_icons/CB.png" /></li>
              <li>
                <img src="/img/payment_method_icons/ApplePay.png" />
              </li>
              <li>
                <img src="/img/payment_method_icons/GooglePay.png" />
              </li>
            </ul>
          </div>
        </div>

        <!-- Gamelib Payment Form -->
        <div id="gamelib_form" v-show="!formRefreshing && methodId == 'new'" />

        <div class="paypay_method" v-show="isPayPayAvailableProduct">
          <div class="paypay_item">
            <input
              type="radio"
              value="paypay"
              id="paypay"
              :disabled="processing || formRefreshing"
              v-model="methodId"
            />
            <label for="paypay" :disabled="processing || formRefreshing">
              <img
                class="payment_method_icon"
                src="/img/payment_method_icons/PayPay.jpg"
              />
              PayPay
            </label>
          </div>
        </div>

        <PrimaryButton
          id="pay_button"
          @click="purchase"
          :disabled="
            !(
              (methodId === 'paypay' && !formRefreshing) ||
              (methodId !== 'paypay' && !processing && !isPayButtonDisabled)
            )
          "
        >
          {{ $t('purchase') }}
        </PrimaryButton>

        <!-- トップページへ戻るボタン -->
        <PrimaryButton
          id="back_button"
          class="back_button"
          color="white"
          @click="cancelAndGoToMypage"
          :disabled="backToMypageBtnDisabled"
        >
          {{ $t('backToTop') }}
        </PrimaryButton>

        <div class="mypage__attentions">
          <p>{{ $t('coinAttention1') }}</p>
          <p>{{ $t('coinAttention2') }}</p>
          <p>{{ $t('order_details__notes__registration_number') }}</p>
          <p>{{ $t('coinAttention3') }}</p>
          <p>{{ $t('coinAttention4') }}</p>
          <p>{{ $t('coinAttention7') }}</p>
          <p v-if="isPayPayAvailableProduct">{{ $t('coinAttention5') }}</p>
        </div>
      </div>

      <charge-limit-dialog
        :show-modal="showChargeLimitDialog"
        :usage="chargeLimitUsage"
        :max-limit="chargeLimitMaxLimit"
        :isGeoIPRegionJP="chargeLimitIsGeoIPRegionJP"
        @close="closeChargeLimitDialog"
      />
    </div>
  </NavigationLayout>
</template>

<script lang="ts">
import { Component, Mixins, Watch } from 'vue-property-decorator'
import NavigationLayout from '@/components/NavigationLayout.vue'
import CardModal, { CardSaveLimit } from '@/components/Mypage/CardModal.vue'
import CoinAmount from '@/components/Mypage/CoinAmount.vue'
import ChargeLimitDialog from '@/components/Mypage/ChargeLimitDialog.vue'
import PrimaryButton from '@/components/PrimaryButton.vue'
import { LanguageQueryHandler } from '@/mixins/LanguageQuery'
import { api } from '@/api/appGatewayServerApi'
import * as models from '@/models'
import Formatter from '@/utils/Formatter'
import { createPayment, createHandler } from '@/services/gamelib'
import {
  showGenericNetworkError,
  handleUnauthorized,
  sendApiWarning,
} from '@/utils/handle_error'
import { checkInMaintenance } from '@/utils/maintenance'
import '@stripe/stripe-js'
import IconImage from '@/components/IconImage.vue'
import { CardErrorCategory, CardErrors } from '@/utils/Payment'
import { EventTracker } from '@/utils/event_tracker'
import { getTokenFromCookie } from '@/utils/token'
import { calculateBonusAmount } from '@/utils/CoinPurchase'
import Cookies from 'js-cookie'
import dayjs from 'dayjs'

@Component({
  components: {
    NavigationLayout,
    CardModal,
    PrimaryButton,
    IconImage,
    CoinAmount,
    ChargeLimitDialog,
  },

  // マイページからの遷移しか許可しない
  beforeRouteEnter(_to, from, next) {
    const transitions = ['mypage']
    if (!from.name || !transitions.includes(from.name)) {
      return next({ name: 'mypage' })
    }
    next()
  },

  // 処理中のブラウザバックは許可しない
  // ただし、エラー文言が表示されている場合は遷移を許可する
  beforeRouteLeave(_to, _from, next) {
    if (
      this instanceof MyPageCoinPurchase &&
      this.processing &&
      !this.hasPaymentError
    ) {
      return next(false)
    }
    next()
  },
})
export default class MyPageCoinPurchase extends Mixins(LanguageQueryHandler) {
  calculateBonusAmount = calculateBonusAmount
  isMonthlyCoinProduct = models.isMonthlyCoinProduct
  isCampaignProduct = models.isCampaignProduct

  product: models.GamelibProduct | null = null
  buyCoins: number = 0
  paymentMethods: models.GamelibPaymentMethod[] = []

  ready: boolean = false
  processing: boolean = false
  formRefreshing: boolean = false
  isPayButtonDisabled: boolean = true
  backToMypageBtnDisabled: boolean = false
  hasPaymentError: boolean = false // エラー文言が表示されているかどうか

  methodId: string = 'new'
  primaryMethodId: string = ''
  showCardModal: boolean = false

  currentPrimaryId: string = ''

  user: models.UserInfo | null = null

  observer: MutationObserver | null = null

  konbiniPaymentMinPrice: number = 0
  isKonbiniPaymentAvailableIPRegion: boolean = false
  isKonbiniPaymentAvailableProduct: boolean = false
  isPayPayAvailableProduct: boolean = false
  state: string = ''
  transactionUrl: string = ''
  purchaseId: string = ''

  isCoinBonusProduct: boolean = false // 次月コイン増量適用商品か
  coinPurchaseBonus: models.CoinPurchaseBonus | null = null
  coinPurchaseBonusRate: number = 0

  // 課金制限（18歳未満の月間課金上限）ダイアログ
  showChargeLimitDialog: boolean = false
  chargeLimitUsage: number = 0
  chargeLimitMaxLimit: number = 0
  // アクセス元IPのGeoIP地域が日本か否か（円/有償コイン表示の出し分け用。check_purchasable レスポンスから取得）
  chargeLimitIsGeoIPRegionJP: boolean = false
  private resolveChargeLimitDialogClosed: (() => void) | null = null

  // purchase() → submitButton.click() の正規ルートを通ったことを通知する関数。
  // createHandler() が返す authorizeNextPurchase をここに保持し、
  // submitButton.click() 直前に呼ぶことでEnterキー等の非正規ルートをブロックする。
  private authorizeNextPurchase: (() => void) | null = null

  // 単一のObserverで複数要素を監視する
  observeGamelibElements() {
    // 既存のMutationObserverインスタンスがある場合は破棄する
    if (this.observer != null) {
      this.observer.disconnect()
      this.observer = null
    }

    // 監視対象の要素をそれぞれ取得
    const targetButton = document.getElementById(
      'payment-submit'
    ) as HTMLInputElement
    const checkbox = document.getElementById(
      'setup-future-usage'
    ) as HTMLInputElement
    const payButton = document.getElementById('pay_button') as HTMLInputElement
    // エラー文言のelement取得
    const paymentError = document.getElementById(
      'payment-error'
    ) as HTMLInputElement

    if (!targetButton || !checkbox || !payButton || !paymentError) {
      // eslint-disable-next-line
      console.error('Failed to get target elements for observation.')
      return
    }

    // 統合されたMutationObserverのコールバック関数
    const mutationCallback = (
      mutationsList: MutationRecord[],
      _observer: MutationObserver
    ) => {
      for (const mutation of mutationsList) {
        // disabled属性の変更を監視
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'disabled'
        ) {
          // gamelib購入ボタンの状態変更を監視
          if (mutation.target === targetButton) {
            // もしgamelib側のボタンがdisabled状態の場合、REALITY側もそれに合わせる
            // （※クレカフォームを表示してる場合のみ）
            if (targetButton.disabled && this.showCardForm) {
              this.isPayButtonDisabled = true
            } else {
              // もしgamelib側のボタンが有効な場合、REALITY側もそれに合わせる
              this.isPayButtonDisabled = false
            }
          }

          // 支払い方法保存チェックボックスの状態変更を監視
          if (mutation.target === checkbox) {
            // StripeForm内で支払い方法が切り替わった時に発火
            const checkboxFormItem = document.getElementById(
              'form-setup-future-usage'
            ) as HTMLInputElement

            if (this.paymentMethods.length >= CardSaveLimit) {
              // クレカ保存上限に達している場合はチェックボックス群を非表示状態にしておく
              // （checkboxをdisabledにすべきだが、監視対象なので行なっていない）
              checkboxFormItem.style.display = 'none'
            } else {
              checkboxFormItem.style.display = 'block'
            }
          }
        }

        // payment-errorのテキスト内容変更を監視
        if (
          mutation.target === paymentError ||
          (paymentError && paymentError.contains(mutation.target as Node))
        ) {
          // childList（子ノードの追加・削除）、characterData（テキストノードの変更）、またはattributes（属性変更）を検知
          if (
            mutation.type === 'childList' ||
            mutation.type === 'characterData' ||
            mutation.type === 'attributes'
          ) {
            this.handlePaymentErrorChange()
          }
        }
      }
    }

    this.observer = new MutationObserver(mutationCallback)

    // disabled属性監視用の設定
    const observerDefaultConfig = {
      attributes: true,
      attributeFilter: ['disabled'], // disabled属性の変更のみを監視
    }

    // 複数の要素を監視開始
    this.observer.observe(targetButton, observerDefaultConfig)
    this.observer.observe(checkbox, observerDefaultConfig)

    // テキスト内容監視用の設定
    const observerTextConfig = {
      childList: true, // 子ノードの変更を監視
      subtree: true, // 子孫ノードの変更も監視
    }

    // payment-error要素が存在する場合のみテキスト変更を監視
    if (paymentError) {
      this.observer.observe(paymentError, observerTextConfig)
    }
  }

  // payment-errorのエラー文言変更を処理するメソッド
  handlePaymentErrorChange() {
    const paymentError = document.getElementById('payment-error')
    if (!paymentError) return
    // エラー文言が変更された時の処理
    const errorText = paymentError.innerText.trim()

    // エラー文言の状態を更新
    this.hasPaymentError = !!errorText

    // 決済処理中の場合はマイページに戻るボタンをdisabledにする
    // ただし、processingがtrueの状態でエラー文言が表示されている場合はマイページに戻るボタンは押せるようにしておく
    // （※GooglePay/ApplePayキャンセル対策）
    if (this.processing) {
      if (errorText) {
        // エラー文言が表示されている場合はマイページに戻るボタンを有効にする
        // processingはそのままにして、hasPaymentErrorフラグでナビゲーションガードを制御
        this.backToMypageBtnDisabled = false
      } else {
        // 決済処理中はマイページに戻るボタンを無効化
        this.backToMypageBtnDisabled = true
      }
    } else {
      this.backToMypageBtnDisabled = false
    }
  }

  changeCursorOnLoading(isLoding: boolean) {
    const payButton = document.getElementById('pay_button') as HTMLInputElement
    payButton.style.cursor = 'auto'
    if (isLoding) {
      payButton.style.cursor = 'wait'
    }
    return
  }

  created() {
    window.addEventListener('beforeunload', this.onBeforeUnload)
  }

  beforeDestroy() {
    window.removeEventListener('beforeunload', this.onBeforeUnload)
    window.removeEventListener('visibilitychange', this.onVisiblityChange)
    // 課金制限ダイアログの待機 Promise を解放（遷移時の宙吊りを防ぐ）
    this.resolveChargeLimitDialogClosed?.()
  }

  onBeforeUnload(event: Event) {
    if (this.processing) {
      event.preventDefault()
    }
  }

  async mounted() {
    try {
      const productId = this.$route.query.productId
      this.isCoinBonusProduct = this.$route.query.isCoinBonusProduct == '1'

      const response = await api().web.mypage.coinPurchase({
        productId: productId as string,
        isBonusProduct: this.isCoinBonusProduct as boolean,
      })

      this.product = response.product
      this.buyCoins = this.product.totalGem
      this.paymentMethods = response.paymentMethods
      this.coinPurchaseBonus = response.coinPurchaseBonus

      // isCoinBonusProductの値をサーバーから取得した状態で上書きしておく
      if (
        this.coinPurchaseBonus &&
        this.coinPurchaseBonus.canPurchaseBonusProduct
      ) {
        this.isCoinBonusProduct = this.coinPurchaseBonus.canPurchaseBonusProduct
        this.coinPurchaseBonusRate = this.coinPurchaseBonus.rate
        // ボーナスコインを加算しておく
        this.buyCoins += this.bonusAmount
      }

      // この商品がコンビニ決済可能の場合
      if (response.konbiniPayment) {
        this.konbiniPaymentMinPrice =
          response.konbiniPayment.konbiniPaymentMinPrice
        // コンビニ決済が利用可能なIP地域か
        this.isKonbiniPaymentAvailableIPRegion =
          response.konbiniPayment.isKonbiniPaymentAvailableIPRegion
        // コンビニ決済が利用可能な商品か
        this.isKonbiniPaymentAvailableProduct =
          response.konbiniPayment.isKonbiniPaymentAvailableProduct
      }

      // この商品がPayPay決済可能の商品か
      this.isPayPayAvailableProduct = response.isPayPayAvailable

      // 購入ステートを保持
      // この段階で別タブなどですでに開かれている決済関連のAPIを無効化する
      this.state = response.state

      this.user = response.userInfo

      // (Stripe)決済フォームの初期化
      await this.initForm()

      // 処理が全て終わったらUI表示
      this.ready = true
    } catch (e) {
      // エラー・メンテの場合はそのままリダイレクトさせたいのでイベント削除する
      window.removeEventListener('beforeunload', this.onBeforeUnload)
      // メンテナンスチェック
      if (checkInMaintenance(e)) {
        // メンテの場合はそのままメンテ中画面へリダイレクトさせたいのでイベント削除する
        this.$router.push({ path: '/maintenance' }).catch(() => {
          // Nothing to do
        })

        return
        // セッションチェックとAPIエラーを表示
      } else if (!(await handleUnauthorized(e))) {
        // メンテナンスでもセッション切れでもなければマイページに戻す
        this.cancelAndGoToMypage()
        return
      }
    }
  }

  get showCardForm() {
    return this.methodId === 'new' || this.paymentMethods.length === 0
  }

  get primaryCard() {
    return (
      this.paymentMethods.find((v) => v.id === this.primaryMethodId) || null
    )
  }

  get bonusAmount() {
    if (!this.product) {
      return 0
    }
    return this.calculateBonusAmount(
      this.product.chargeGem, // 有償コイン
      this.coinPurchaseBonusRate
    )
  }

  get coinFreeAndBonusAmount() {
    if (!this.product) {
      return 0
    }
    return Formatter.numberWithCommas(this.product.chargeGem + this.bonusAmount)
  }

  // (Stripe)決済フォームの初期化
  async initForm() {
    // (Stripe) 決済の初期化
    const methodId = await this.initPayment()

    // 選択中の決済手段を更新
    this.methodId = methodId

    // 決済手段の初期値を更新
    this.updatePrimary(methodId)
    this.currentPrimaryId = methodId

    // gamelib要素の統合監視を開始
    this.observeGamelibElements()
  }

  // (Stripe)決済手段の初期化
  async initPayment() {
    if (!this.product || this.formRefreshing) return this.methodId
    this.formRefreshing = true
    const productId = this.product.productId

    try {
      // 下記のformPromiseの解決を待つためにさらにPromiseで囲う
      // これにより、決済手段のラジオボタンが意図しないタイミングで上書きされて表示が崩れるのを防ぐ
      const methodId = await new Promise<string>((resolve, reject) => {
        // このPromiseはawaitしてないので解決タイミングに注意
        const formPromise = api()
          .streamer.currency.coin.purchase._({
            productId: productId,
            providerId: models.ProviderIdTypes.Stripe,
            state: this.state,
            isWebView: getTokenFromCookie() !== undefined,
            isCoinBonusProduct: this.isCoinBonusProduct,
          })
          .then((payload) => {
            const method = payload.entry.payment_method
            if (method) {
              resolve(method.id)
            } else {
              // 決済手段が未登録の場合は'new'を返す
              resolve('new')
            }

            this.purchaseId = payload.entry.purchase_id

            return payload.entry
          })
          .catch((e) => {
            // ここでのエラーはcreatePaymentのPromiseでエラーハンドリングするため、そのまま返す
            return e
          })

        const { handler, authorizeNextPurchase } = createHandler({
          onReady: this.onPurchaseReady,
          onCompleted: this.onPurchaseCompleted,
          onFailure: this.onPurchaseFailed,
          onPurchaseFinished: this.onPurchaseFinished,
          onSetupOrder: this.onSetupOrder,
          onUnauthorizedSubmit: this.onUnauthorizedSubmit,
        })
        this.authorizeNextPurchase = authorizeNextPurchase

        createPayment(formPromise, handler)
          .then(async (payment) => {
            await payment.mount('#gamelib_form')
            // UIに合わせてgamelib formのDOMを操作
            this.hideElement('form-use-saved-paymethod')
            this.hideElement('payment-submit')
            this.setCardFormState()

            const elem = document.getElementById('form-setup-future-usage')
            if (elem) {
              const elems = elem.getElementsByClassName('label-checkbox')
              if (elems[0]) {
                elems[0].innerHTML = this.$t(
                  'order_details__button__payment_method_registration'
                ).toString()
              }
            }
          })
          .catch((e) => {
            reject(e)
          })
      })

      return methodId
    } catch (e) {
      // エラーの場合はそのままリダイレクトさせたいのでイベント削除する
      window.removeEventListener('beforeunload', this.onBeforeUnload)

      // メンテナンスチェック
      if (checkInMaintenance(e)) {
        alert(this.$t('order_details__alert__cancel_operation'))
        this.$router.push({ path: '/maintenance' }).catch(() => {
          // Nothing to do
        })
        // セッションチェックとAPIエラーを表示
      } else if (!(await handleUnauthorized(e))) {
        // メンテナンスでもセッション切れでもなければマイページに戻す
        this.cancelAndGoToMypage()
      }
    } finally {
      this.formRefreshing = false
    }

    return 'new'
  }

  // 課金制限チェック。購入可なら true、購入不可なら false（呼び出し側で中断）。
  // 月間上限ブロック・同時決済/連続課金のロック・チェック自体の失敗（ネットワークエラー等）は
  // すべて購入不可（false）として扱い、購入を中断する。
  // purchaseId: purchase._() で発行済みの purchase_id を渡す。
  //   冪等処理で同一 purchase_id の予約が既にある場合に新規予約を作らない（二重予約を防ぐ）。
  async checkChargeLimitPurchasable(
    providerId: number,
    purchaseId: string
  ): Promise<boolean> {
    if (!this.product) return true
    try {
      const result = await api().web.coin.checkPurchasable({
        productId: this.product.productId,
        providerId,
        purchaseId,
      })
      if (!result.isPurchasable) {
        await this.openChargeLimitDialog(
          result.usage,
          result.maxLimit,
          result.isGeoIPRegionJP
        )
        return false
      }
      return true
    } catch (e) {
      // check_purchasable のエラーは購入を止める（握りつぶして続行しない）。
      // 同時決済/連続課金のロックによる拒否もここに来るため、汎用エラーを表示して中断する。
      showGenericNetworkError()
      return false
    }
  }

  // 課金制限ダイアログを表示し、閉じられるまで待機する
  openChargeLimitDialog(
    usage: number,
    maxLimit: number,
    isGeoIPRegionJP: boolean
  ): Promise<void> {
    // 既に待機中の Promise があれば先に解決してリークを防ぐ
    if (this.resolveChargeLimitDialogClosed) {
      this.resolveChargeLimitDialogClosed()
      this.resolveChargeLimitDialogClosed = null
    }
    this.chargeLimitUsage = usage
    this.chargeLimitMaxLimit = maxLimit
    this.chargeLimitIsGeoIPRegionJP = isGeoIPRegionJP
    this.showChargeLimitDialog = true
    return new Promise((resolve) => {
      this.resolveChargeLimitDialogClosed = resolve
    })
  }

  closeChargeLimitDialog() {
    this.showChargeLimitDialog = false
    if (this.resolveChargeLimitDialogClosed) {
      this.resolveChargeLimitDialogClosed()
      this.resolveChargeLimitDialogClosed = null
    }
  }

  async onPurchaseReady() {
    try {
      await api().streamer.currency.coin.purchase.ready({
        state: this.state,
      })

      return true
    } catch (e) {
      showGenericNetworkError()
    }

    try {
      // 処理を中断して遷移
      this.processing = false
      this.backToMypageBtnDisabled = false
      await this.cancelAndGoToMypage()
    } catch (err) {
      // Nothing to do
    }

    return false
  }

  async onUnauthorizedSubmit() {
    // purchase() を経由しない非正規 submit（Enterキー等）を検知。
    // 汎用エラーを表示してマイページへ戻す。
    showGenericNetworkError()
    try {
      this.processing = false
      this.backToMypageBtnDisabled = false
      await this.cancelAndGoToMypage()
    } catch (err) {
      // Nothing to do
    }
  }

  onPurchaseCompleted() {
    this.changeCursorOnLoading(false)
    this.processing = false
    try {
      this.$router
        .push({
          name: 'coin_purchase_complete',
          query: {
            purchaseId: this.purchaseId,
            purchasedCoins: this.buyCoins.toString(),
          },
        })
        .catch(() => {})
    } catch (err) {
      // Nothing to do
    }
  }

  async onPurchaseFinished(
    purchaseId: string,
    _info: gamelib.webpay.model.IPurchaseInfo,
    receipt: gamelib.webpay.model.IReceipt
  ) {
    // NOTE:コンビニ決済は日本のみの決済方法
    if (
      receipt.payment_method_type === 'konbini' &&
      receipt.status === 'requires_action'
    ) {
      this.changeCursorOnLoading(false)
      this.processing = false
      alert(this.$t('convenience_store_payment_order_completed__body'))
      this.$router
        .push({
          path: '/mypage/coin_purchase/pending/' + purchaseId,
        })
        .catch(() => {
          // Nothing to do
        })
      return
    }
  }

  onSetupOrder(purchaseId: string, info: gamelib.webpay.model.IPurchaseInfo) {
    if (!this.product) return

    // 課金ログをFirebase Analyticsに送信する
    // onPurchaseCompleted, onPurchaseFinishedだと全ての決済手段でコールされることが保証されていない
    // 例えばコンビニ決済ではonPurchaseCompleted、それ以外の決済手段ではonPurchaseFinishedがコールされない
    // よって、全ての決済手段でコールされるonSetupOrderのタイミングで実施
    EventTracker.default.trackPurchase(
      this.product.productId,
      this.product.name,
      this.product.currency,
      this.product.price,
      info.paymentIntent.id,
      info.payment_method_type || '',
      purchaseId
    )
  }

  onPurchaseFailed(e: gamelib.webpay.model.IPurchaseError) {
    this.changeCursorOnLoading(false)
    this.processing = false
    this.backToMypageBtnDisabled = false
    alert(this.getCardErrorMessage(e.code, e.decline_code))
    // エラー時はエラーメッセージのダイアログ表示後マイページに戻す
    this.cancelAndGoToMypage()
  }

  updateCardSaveCheckbox() {
    // チェックボックス取得
    const checkbox = document.getElementById(
      'setup-future-usage'
    ) as HTMLInputElement

    // 「クレジットカードはこれ以上保存できません」部分の要素を取得
    const checkboxErrorText = document.getElementById(
      'setup-future-usage-error'
    ) as HTMLInputElement

    if (this.paymentMethods.length < CardSaveLimit) {
      if (checkbox.disabled) {
        // クレカ保存上限に未達でdisabled状態なら解除する
        checkbox.disabled = false
        // 「クレジットカードはこれ以上保存できません」を非表示にしておく
        checkboxErrorText.style.display = 'none'
      }
      return
    }

    // クレカ保存上限に達している場合はdisabled状態にしておく
    checkbox.checked = false
    checkbox.disabled = true
    // 「クレジットカードはこれ以上保存できません」を表示しておく
    checkboxErrorText.style.display = 'block'
  }

  openCardModal() {
    if (!this.showCardForm) {
      this.showCardModal = true
    }
  }

  closeCardModal() {
    this.showCardModal = false
  }

  updateMethods(methods: models.GamelibPaymentMethod[]) {
    this.paymentMethods = methods
  }

  updatePrimary(primaryId: string) {
    this.primaryMethodId = primaryId
  }

  async purchase() {
    // ローカルストレージに保存されているマイページから遷移時の時刻が取得できた場合は現在時刻と比較する
    const moveToPurchaseTimeStr = localStorage.getItem('moveToPurchaseTime')
    if (moveToPurchaseTimeStr) {
      const now = dayjs().tz('Asia/Tokyo')
      const moveToPurchaseTime = dayjs(moveToPurchaseTimeStr).tz('Asia/Tokyo')
      // 日付が変わっている場合はマイページに戻す
      if (now.format('YYYYMMDD') !== moveToPurchaseTime.format('YYYYMMDD')) {
        localStorage.removeItem('moveToPurchaseTime')
        alert(this.$t('errorGeneral'))
        this.cancelAndGoToMypage()
        return
      }
    }

    // PayPayの場合は別処理
    if (this.methodId === 'paypay') {
      await this.purchasePayPay()
      return
    }

    // カード保存上限を超えて保存させないよう送信前にチェックボックス状態を見る
    this.updateCardSaveCheckbox()

    const product = this.product
    if (!product) return

    const errorText = this.getErrorText()
    if (errorText) {
      alert(errorText)
      return
    }

    const submitButton = document.getElementById(
      'payment-submit'
    ) as HTMLButtonElement
    if (!submitButton || submitButton.disabled) {
      return
    }

    if (this.processing) return
    this.processing = true
    this.backToMypageBtnDisabled = true
    // カーソルをwaitへ変更
    this.changeCursorOnLoading(true)

    try {
      let total = this.formatNumber(product.totalGem)
      let free = this.formatNumber(product.freeGem)
      const charge = this.formatNumber(product.chargeGem)

      if (this.isCoinBonusProduct && this.bonusAmount > 0) {
        // ボーナスコインを加算しておく（chargeは有料コインなので加算しない）
        total = this.formatNumber(product.totalGem + this.bonusAmount)
        free = this.formatNumber(product.freeGem + this.bonusAmount)
      }

      const confirmMsg = this.$t('purchaseConfirm', {
        total,
        charge,
        free,
        price: product.formattedPrice,
      })

      if (!confirm(confirmMsg.toString())) {
        this.changeCursorOnLoading(false)
        this.processing = false
        this.backToMypageBtnDisabled = false
        return
      }

      // 決済ボタン押下前に念の為メンテナンスチェック
      // ※以下メンテナンスチェックするだけの為のリクエスト（503の場合catchで処理する）
      await api().maintenance.allCheck()

      // 課金制限チェック（購入ボタン押下時。submitButton.click() より前）
      // this.purchaseId は initPayment() → purchase._() で発行済みのため必ず非空
      const purchasable = await this.checkChargeLimitPurchasable(
        models.ProviderIdTypes.Stripe,
        this.purchaseId
      )
      if (!purchasable) {
        this.changeCursorOnLoading(false)
        this.processing = false
        this.backToMypageBtnDisabled = false
        return
      }

      // コインボーナス適用商品の場合に購入履歴のstatusを決済処理中に更新しておく
      if (this.isCoinBonusProduct) {
        await api().streamer.currency.coin.purchase.coin_bonus_product.ready({
          isCoinBonusProduct: this.isCoinBonusProduct,
          purchaseId: this.purchaseId,
        })
      }

      // gamelib 決済フォームのボタンを押下
      // 完了処理は handler で行うのでここでは processing = true のまま
      // readyForPurchase で purchaseAuthorized を確認するため、click() 前にセットする
      this.authorizeNextPurchase?.()
      submitButton.click()
    } catch (e) {
      this.changeCursorOnLoading(false)
      this.processing = false
      this.backToMypageBtnDisabled = false
      // メンテナンスチェック
      if (checkInMaintenance(e)) {
        // メンテの場合はそのままメンテ中画面へリダイレクトさせたいのでイベント削除する
        window.removeEventListener('beforeunload', this.onBeforeUnload)
        alert(this.$t('order_details__alert__cancel_operation'))
        this.$router.push({ path: '/maintenance' }).catch(() => {
          // Nothing to do
        })
        return
        // セッションチェックとAPIエラーを表示
      } else if (!(await handleUnauthorized(e))) {
        // メンテナンスでもセッション切れでもなければマイページに戻す
        await this.cancelAndGoToMypage()
        return
      }
    }
  }

  async purchasePayPay() {
    const product = this.product
    if (!product) return

    const errorText = this.getErrorText()
    if (errorText) {
      alert(errorText)
      return
    }

    if (this.processing) return
    this.processing = true
    this.backToMypageBtnDisabled = true
    // カーソルをwaitへ変更
    this.changeCursorOnLoading(true)

    try {
      let total = this.formatNumber(product.totalGem)
      let free = this.formatNumber(product.freeGem)
      const charge = this.formatNumber(product.chargeGem)

      if (this.isCoinBonusProduct && this.bonusAmount > 0) {
        // ボーナスコインを加算しておく（chargeは有料コインなので加算しない）
        total = this.formatNumber(product.totalGem + this.bonusAmount)
        free = this.formatNumber(product.freeGem + this.bonusAmount)
      }

      const confirmMsg = this.$t('purchaseConfirm', {
        total,
        charge,
        free,
        price: product.formattedPrice,
      })

      if (!confirm(confirmMsg.toString())) {
        this.changeCursorOnLoading(false)
        this.processing = false
        this.backToMypageBtnDisabled = false
        return
      }

      // 決済ボタン押下前に念の為メンテナンスチェック
      // ※以下メンテナンスチェックするだけの為のリクエスト（503の場合catchで処理する）
      await api().maintenance.allCheck()

      const response = await api().streamer.currency.coin.purchase._({
        productId: product.productId,
        providerId: models.ProviderIdTypes.PayPay,
        state: this.state,
        isWebView: this.isWebView(),
        isCoinBonusProduct: this.isCoinBonusProduct,
      })

      this.purchaseId = response.entry.purchase_id
      const transactionUrl = response.entry.transaction_url

      // 課金制限チェック（購入ボタン押下時。purchase.ready() より前）
      // this.purchaseId は purchase._() で発行済みのため必ず非空
      const purchasable = await this.checkChargeLimitPurchasable(
        models.ProviderIdTypes.PayPay,
        this.purchaseId
      )
      if (!purchasable) {
        // PayPay は purchase._() 時点で gamelib にオーダーが作られているためキャンセルする
        try {
          await api().streamer.currency.coin.purchase.postpaid.cancel({
            purchaseId: this.purchaseId,
            providerId: models.ProviderIdTypes.PayPay,
          })
        } catch (e) {
          // キャンセル失敗は無視（サーバ側の postpaid/cancel → pending_order_payments 管理に委ねる）
        }
        this.changeCursorOnLoading(false)
        this.processing = false
        this.backToMypageBtnDisabled = false
        return
      }

      // CookieにtransactionUrlを保持しておく
      Cookies.set('paypay_transaction_url', transactionUrl, {
        path: '/',
        expires: 5 / (24 * 60), // 5分
        secure: true,
      })

      // コインボーナス適用商品の場合に購入履歴のstatusを決済処理中に更新しておく
      if (this.isCoinBonusProduct) {
        await api().streamer.currency.coin.purchase.coin_bonus_product.ready({
          isCoinBonusProduct: this.isCoinBonusProduct,
          purchaseId: this.purchaseId,
        })
      }

      // ほぼ確認は不要だが、Stripeの決済フローと合わせるために一応呼んでおく
      await api().streamer.currency.coin.purchase.ready({
        state: this.state,
      })

      // 課金ログをFirebase Analyticsに送信する
      // PayPayではこのタイミングでは購入が確定していないが、Firebase Analyticsには送信しておく
      EventTracker.default.trackPurchase(
        product.productId,
        product.name,
        product.currency,
        product.price,
        this.purchaseId, // PayPayではtransaction_idがないためpurchase_idを代用
        'paypay',
        this.purchaseId
      )

      window.addEventListener('visibilitychange', this.onVisiblityChange)

      // PayPay決済画面へ遷移
      window.removeEventListener('beforeunload', this.onBeforeUnload)
      location.href = transactionUrl
    } catch (e) {
      // 処理は完了したので遷移を許可する
      this.processing = false
      this.backToMypageBtnDisabled = false
      // エラーの場合はそのままリダイレクトさせたいのでイベント削除する
      window.removeEventListener('beforeunload', this.onBeforeUnload)

      // メンテナンスチェック
      if (checkInMaintenance(e)) {
        alert(this.$t('order_details__alert__cancel_operation'))
        this.$router.push({ path: '/maintenance' }).catch(() => {
          // Nothing to do
        })
        // セッションチェックとAPIエラーを表示
      } else if (!(await handleUnauthorized(e))) {
        // メンテナンスでもセッション切れでもなければマイページに戻す
        await this.cancelAndGoToMypage()
      }
    }
  }

  onVisiblityChange() {
    // WebView表示かつ、PayPayアプリで決済の場合、redirectはRealityアプリがアクティブになるだけ(Universal Link)
    // アクティブになったときに1秒後にcommitページへ遷移する準備をする
    const moveToCommit = () => {
      const url = `/mypage/coin_purchase/check?purchase_id=${this.purchaseId}&state=${this.state}`

      // router.pushだとWebView内での遷移がうまくいかないため、location.hrefを使用
      window.removeEventListener('beforeunload', this.onBeforeUnload)
      location.href = url
    }

    if (document.visibilityState === 'visible') {
      setTimeout(moveToCommit, 100)
    }
  }

  @Watch('methodId')
  onPaymentMethodChange() {
    if (!this.ready) return
    this.setCardFormState()
  }

  // 保存済みクレカ使用のチェックボックス制御
  changeUseSavedMethodCheck(checked: boolean) {
    const useSavedCheck = document.getElementById(
      'use-saved-paymethod'
    ) as HTMLInputElement

    if (!useSavedCheck) throw new Error('check error')
    if (useSavedCheck.checked !== checked) {
      useSavedCheck.click()
    }
  }

  setCardFormState() {
    if (this.showCardForm) {
      // 保存済みクレカ使用のチェックを外し、カード関連フォームを表示
      this.changeUseSavedMethodCheck(false)
      this.showElement('payment-element')
      this.showElement('form-setup-future-usage')
    } else {
      // 保存済みクレカ使用にチェックを入れ、カード関連フォームを隠す
      this.changeUseSavedMethodCheck(true)
      this.hideElement('payment-element')
      this.hideElement('form-setup-future-usage')
    }
  }

  getErrorText() {
    if (!this.showCardForm) return ''
    const elem = document.getElementById('payment-error')
    return elem ? elem.innerText : ''
  }

  hideElement(id: string) {
    const elem = document.getElementById(id)
    if (elem) elem.classList.add('hide')
  }

  showElement(id: string) {
    const elem = document.getElementById(id)
    if (elem) elem.classList.remove('hide')
  }

  getCardInfo = models.getCardInfo
  getPaymentMethodImg = models.getPaymentMethodImg

  formatNumber(num: number) {
    return Formatter.numberWithCommas(num)
  }

  // cancelAndGoToMypage 購入成功以外でマイページへ戻る際に呼ぶ。
  // purchaseId が取得済みであれば課金制限の予約を解放してからマイページへ遷移する。
  // 解放失敗は無視（過剰カウント側に倒れるため安全。ReconcileReservations で次回チェック時に解放される）。
  async cancelAndGoToMypage() {
    if (this.purchaseId) {
      try {
        await api().web.coin.purchase.cancel({ purchaseId: this.purchaseId })
      } catch (e) {
        // 解放失敗は無視してマイページへ遷移する
      }
    }
    this.$router.push({ path: '/mypage' }).catch(() => {
      // Nothing to do
    })
  }

  backToTop() {
    this.$router.push({ name: 'mypage' }).catch(() => {
      // Nothing to do
    })
  }

  get localeClass() {
    return '-' + this.$i18n.locale
  }

  getCardErrorMessage(errorCode?: string, declineCode?: string) {
    const errorCategory: string = this.getCardErrorCategory(
      errorCode,
      declineCode
    ) as string
    return this.$t(errorCategory).toString()
  }

  getCardErrorCategory(
    errorCode?: string,
    declineCode?: string
  ): CardErrorCategory {
    // errorCodeとdeclineCodeの和集合から探す
    const errorCategory: CardErrorCategory | undefined = declineCode
      ? CardErrors.get(declineCode)
      : CardErrors.get(errorCode!)
    return errorCategory ? errorCategory : CardErrorCategory.cardRetryError
  }

  async updatePrimaryCard(paymentMethodId: string) {
    if (this.currentPrimaryId === paymentMethodId) {
      // もしエラーメッセージが残っている場合は消しておく
      const elem = document.getElementById('payment-error')
      if (elem && elem.innerText !== '') {
        elem.innerText = ''
      }
      return
    }
    if (this.processing) return
    this.processing = true
    this.backToMypageBtnDisabled = true

    try {
      await api().streamer.currency.coin.method.primary({
        paymentMethodId,
      })
    } catch (e) {
      if (checkInMaintenance(e)) {
        // メンテの場合はそのままメンテ中画面へリダイレクトさせたいのでイベント削除する
        window.removeEventListener('beforeunload', this.onBeforeUnload)
        alert(this.$t('order_details__alert__cancel_operation'))
        // 強制的にリダイレクトさせる
        //（ページ遷移ですが、ここでは$router.pushが作動しないので、location.hrefを使用してます）
        window.location.href = '/maintenance'
        return
      }
      sendApiWarning(e)
      showGenericNetworkError()
    }

    // カードが変更される度に初期化させる
    await this.initForm()

    this.processing = false
    this.backToMypageBtnDisabled = false
  }

  isWebView() {
    return getTokenFromCookie() !== undefined
  }

  getPaymentMethodText() {
    if (this.isKonbiniPaymentAvailableProduct) {
      return this.$t(`order_details__payment_method__card_konbini`)
    }

    return this.$t(`order_details__payment_method__card`)
  }
}
</script>

<style scoped>
.payment_method {
  margin-top: 32px;
  margin-bottom: 8px;
  display: flex;

  & span.edit {
    color: #188dff;
    display: inline-block;
    margin: 16px 0 0 auto;
    cursor: pointer;
  }

  & span.select_link_disabled {
    cursor: not-allowed;
  }

  & img {
    display: inline;
    width: 14px;
    height: 14px;
    vertical-align: text-bottom;
    color: #188dff;
  }

  & .icon_pen {
    position: relative;
    background-image: url('/img/pen.svg');
  }
}

.payment_explanation {
  margin: 0 0 24px 0;

  & div {
    margin-bottom: 12px;
  }

  & .available_payment_methods {
    background-color: #e8e8e8;
    margin: 0 0 16px 0px;
    border-radius: 5px;

    & img {
      width: 40px;
      margin-right: 6px;
      border-radius: 3px;
    }

    & ul {
      display: flex;
      justify-content: left;
      margin-left: -28px;
      padding-top: 12px;
      padding-bottom: 12px;
    }

    & li {
      list-style: none;
    }
  }

  @media (max-width: 464px) {
    .available_payment_methods {
      & p {
        padding-bottom: 4px;
      }

      & ul {
        flex-wrap: wrap;
        padding-top: 4px;
      }

      & li {
        padding-top: 8px;
      }
    }
  }
}

.product_heading {
  font-size: 16px;
  line-height: 20px;
  font-weight: bold;
  margin: 16px 0 8px 0;
}

.product_value {
  font-size: 16px;
  line-height: 20px;
}

.coin_info_paid_free {
  font-size: 10px;
  font-weight: 300;
  color: #bababa;
  margin: 10px 0px 0px 0px;
}

.select_item {
  height: 24px;
  text-align: left;
  font-size: 14px;
  line-height: 20px;
}

.select_item input[type='radio'] {
  width: 14px;
  height: 14px;
  vertical-align: middle;
  cursor: pointer;
}

.select_item label {
  margin: 0 0 0 6px;
  vertical-align: middle;
  font-size: 14px;
  line-height: 20px;
  font-style: normal;
  font-weight: 600;
  height: 20px;
  font-family: 'Hiragino Sans';
  color: #0f0f0f;

  cursor: pointer;
}

.select_item label.disabled {
  color: red;
}

.select_link {
  display: inline-block;
  color: #188dff;
  height: 20px;
  margin-bottom: 24px;
}

.select_method {
  margin-bottom: 12px;
}

.select_link_disabled {
  opacity: 0.38;
}

.card_info {
  width: 80%;
  height: 32px;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  margin: 8px 0;
  font-size: 14px;
  line-height: 20px;
  padding: 6px 10px;
}

#pay_button {
  width: 100%;
  margin: 26px 0 16px 0;
  border: none;
}

#mypage__wrapper {
  position: relative;
  height: auto;
  min-height: 100%;
}

#mypage__container {
  font-family: sans-serif;
  font-size: 14px;
  line-height: 1.43;
  color: #262626;
  padding: 0 16px 40px 16px;
  max-width: 800px;
  margin: 0 auto;
}

.mypage__attentions {
  padding: 0 14px;
  margin: 8px 0 14px;
  font-size: 12px;
  line-height: 150%;
  color: #808080;
}

.title {
  width: 100%;
  margin: 16px 0 16px 0;
  display: flex;
  justify-content: center;
  align-items: center;

  & p {
    font-size: larger;
    font-weight: bold;
    font-family: 'Hiragino Sans';
    margin-bottom: 16px;
  }
}

.account {
  display: flex;
  align-items: center;

  & .label {
    margin-right: 20px;
  }

  & .icon {
    margin-right: 8px;
  }

  & b {
    min-width: fit-content;
    margin-right: auto;
  }

  & .info {
    display: flex;
    margin: 0 0 0 auto;

    & .username {
      flex: 1;
      max-width: 400px;
      margin: auto;
    }
  }
}

.product {
  display: flex;
  align-items: center;

  & .label {
    margin-right: 20px;
  }

  & b {
    min-width: fit-content;
    margin-right: auto;
  }

  & .info {
    display: flex;
    margin: 0 0 0 auto;

    & .price {
      flex: 1;
      max-width: 400px;
      margin: auto;
    }
  }
}

.card_item {
  text-align: left;
  vertical-align: middle;
  margin: 0 0 24px 0;

  & label {
    margin: 0 0 0 6px;
    font-style: normal;
    font-weight: 600;
    height: 24px;
    font-family: 'Hiragino Sans';
    color: #0f0f0f;
    cursor: pointer;
  }

  & input {
    width: 14px;
    height: 14px;
    vertical-align: middle;
    cursor: pointer;
  }

  & img {
    cursor: pointer;
    height: 24px;
    margin: 0 8px 0 0;
    border: 1px solid #e8e8e8;
  }
}

.paypay_method {
  margin-top: 24px;
  margin-bottom: 0px;
}

.paypay_item {
  text-align: left;
  vertical-align: middle;
  margin: 0 0 24px 0;

  & label {
    margin: 0 0 0 6px;
    font-style: normal;
    font-weight: 600;
    height: 24px;
    font-family: 'Hiragino Sans';
    color: #0f0f0f;
    cursor: pointer;
  }

  & input {
    width: 14px;
    height: 14px;
    vertical-align: middle;
    cursor: pointer;
  }

  & img {
    cursor: pointer;
    height: 24px;
    margin: 0 0px 0 0;
  }
}

.coin_purchase_bonus_info {
  background-color: #d4eaff;
  border-radius: 4px;
  padding: 0px 4px 0px 4px;

  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.coin_purchase_bonus_info_text {
  font-size: 10px;
  font-weight: 300;
  color: #188dff;
}

.el_konbiniPayment_text {
  color: #808080;
}

.el_monthlyGradeBonus_text {
  color: #ff003d;
}

.back_button {
  margin-bottom: 16px;
}
</style>

<!-- global style for gamelib form -->
<style>
.hide {
  display: none;
}

#form-setup-future-usage {
  margin-top: 24px;
}

#form-setup-future-usage input {
  width: 24px;
  height: 24px;
  vertical-align: middle;
}

#form-setup-future-usage label {
  margin: 0 0 0 4px;
  font-size: 14px;
  line-height: 24px;
  vertical-align: middle;
}

#setup-future-usage-error {
  margin-top: 8px;
}

/* ------ StripeForm Design CSS by Gamelib ------------ */

#gamelib-payment-form .hidden {
  display: none;
}

#gamelib-payment-form #payment-message {
  color: rgb(105, 115, 134);
  font-size: 16px;
  line-height: 20px;
  padding-top: 12px;
  text-align: center;
}

#gamelib-payment-form #payment-element {
  margin-bottom: 24px;
}

/* Buttons and links */
#gamelib-payment-form button {
  display: none;
}

#gamelib-payment-form button:hover {
  filter: contrast(115%);
}

#gamelib-payment-form button:disabled {
  opacity: 0.5;
  cursor: default;
}

/* spinner/processing state, errors */
#gamelib-payment-form .spinner,
#gamelib-payment-form .spinner:before,
#gamelib-payment-form .spinner:after {
  border-radius: 50%;
}

#gamelib-payment-form .spinner {
  color: #ffffff;
  font-size: 22px;
  text-indent: -99999px;
  margin: 0px auto;
  position: relative;
  width: 20px;
  height: 20px;
  box-shadow: inset 0 0 0 2px;
  -webkit-transform: translateZ(0);
  -ms-transform: translateZ(0);
  transform: translateZ(0);
}

#gamelib-payment-form .spinner:before,
#gamelib-payment-form .spinner:after {
  position: absolute;
  content: '';
}

#gamelib-payment-form .spinner:before {
  width: 10.4px;
  height: 20.4px;
  background: #5469d4;
  border-radius: 20.4px 0 0 20.4px;
  top: -0.2px;
  left: -0.2px;
  -webkit-transform-origin: 10.4px 10.2px;
  transform-origin: 10.4px 10.2px;
  -webkit-animation: loading 2s infinite ease 1.5s;
  animation: loading 2s infinite ease 1.5s;
}

#gamelib-payment-form .spinner:after {
  width: 10.4px;
  height: 10.2px;
  background: #5469d4;
  border-radius: 0 10.2px 10.2px 0;
  top: -0.1px;
  left: 10.2px;
  -webkit-transform-origin: 0px 10.2px;
  transform-origin: 0px 10.2px;
  -webkit-animation: loading 2s infinite ease;
  animation: loading 2s infinite ease;
}

@-webkit-keyframes loading {
  0% {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }

  100% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}

@keyframes loading {
  0% {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }

  100% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}

@media only screen and (max-width: 368px) {
  form {
    width: 80vw;
    min-width: initial;
  }
}

/* ------ StripeForm Design CSS by Gamelib ------------ */
</style>

<!-- i18n patch styles -->

<style>
.-fr #form-setup-future-usage label,
.-ru #form-setup-future-usage label {
  font-size: 9px;
  letter-spacing: inherit;
}

.-fr #form-setup-future-usage input[type='checkbox'],
.-ru #form-setup-future-usage input[type='checkbox'] {
  width: 14px;
}
</style>
